import View from '@ckeditor/ckeditor5-ui/src/view';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';
import { KeystrokeHandler } from 'ckeditor5/src/utils';
import LabeledInputView from '@ckeditor/ckeditor5-link/src/ui/labeledinputview';
import GroupedItemsView from '@ckeditor/ckeditor5-link/src/ui/groupeditemsview';
import { VideoPropertiesIcons } from './video-properties-icons';

const PositionsMap = new Map([
	['Normal', VideoPropertiesIcons.positionAuto],
	['Float Left', VideoPropertiesIcons.positionLeft],
	['Float Right', VideoPropertiesIcons.positionRight],
	['Center Block', VideoPropertiesIcons.positionCenter],
]);

const SizeMap = new Map([
	['Auto Size', VideoPropertiesIcons.positionAuto],
	['Small Video', VideoPropertiesIcons.sizeSmall],
	['Medium Video', VideoPropertiesIcons.sizeMedium],
	['Large Video', VideoPropertiesIcons.sizeLarge],
]);

const OptionsMap = new Map([
	['Show Controls', VideoPropertiesIcons.optionControls],
	['Auto Play', VideoPropertiesIcons.optionPlay],
	['Loop Video', VideoPropertiesIcons.optionLoop],
	['Mute Video', VideoPropertiesIcons.optionMute],
	['No Tracking', VideoPropertiesIcons.optionNoTracking],
]);

export default class VideoPropertiesFormView extends View {
	constructor(locale, command, config) {
		super(locale);
		const t = locale.t;

		this.keystrokes = new KeystrokeHandler();

		const classList = [
			'ck',
			'ck-link-form',
			'ck-responsive-form',
			'ck-video-properties-form',
		];
		this.command = command;

		this.closeButtonView = this._createButton(
			t('Close'),
			'ck-button',
			'close'
		);
		this.deleteButtonView = this._createButton(
			t('Delete'),
			'ck-button',
			'delete',
			VideoPropertiesIcons.trash
		);

		this.videoUrlInputView = this._createInputUrlView(
			'Video URL',
			config.mediaChoosers
		);
		this.videoPosterInputView = this._createInputUrlView(
			'Video Poster',
			config.imageChoosers
		);
		this.positionButtonView = this._createButtonView(
			PositionsMap,
			'videoPosition'
		);
		this.sizeButtonView = this._createButtonView(SizeMap, 'videoSize');
		this.optionButtonView = this._createButtonView(
			OptionsMap,
			'videoOptions',
			true
		);
		this.children = this._createFormChildren();

		this.videoUrlInputView.fieldView
			.bind('value')
			.to(this.command, 'source');

		this.videoPosterInputView.fieldView
			.bind('value')
			.to(this.command, 'poster');

		this.command
			.bind('source')
			.to(this.videoUrlInputView.fieldView, 'value', (value) => {
				// if the url is the same then do nothing.
				if (JSON.stringify(this.command.source) == JSON.stringify(value))
					return value;

				this.command.source = value;
				this.command.execute('source');
				// T# TODO need to restore the options
				return value;
			});

		this.command
			.bind('poster')
			.to(this.videoPosterInputView.fieldView, 'value', (value) => {
				// if the url is the same then do nothing.
				if (JSON.stringify(this.command.poster) == JSON.stringify(value))
					return value;

				this.command.poster = value;
				this.command.execute('poster');
				return value;
			});
		this.setTemplate({
			tag: 'form',
			attributes: {
				class: classList,
				tabindex: '-1',
			},
			children: this.children,
		});
	}

	_createFormChildren() {
		const children = this.createCollection();
		children.add(this.videoUrlInputView);
		children.add(this.videoPosterInputView);

		const buttonsGroup = new GroupedItemsView(this.locale, [
			this.positionButtonView,
			this.sizeButtonView,
			this.optionButtonView,
			this.deleteButtonView,
			this.closeButtonView,
		]);
		children.add(buttonsGroup);
		//children.add( this.closeButtonView );

		return children;
	}

	_createButtonView(map, binding, toggleGroup) {
		const t = this.locale.t;
		const buttons = [];

		for (let [key, value] of map) {
			const button = toggleGroup
				? this._createToggleButtonGroup(t(key), value, binding)
				: this._createToggleButton(t(key), value, binding);
			buttons.push(button);
		}
		const group = new GroupedItemsView(this.locale, buttons, 'ck-toolbar');
		return group;
	}

	_createInputUrlView(label, choosers) {
		const t = this.locale.t;
		const labeledInput = new LabeledInputView(this.locale, choosers);
		labeledInput.label = t(label);

		return labeledInput;
	}

	// create toggle button group
	_createToggleButtonGroup(label, icon, binding) {
		const button = new ButtonView(this.locale);
		button.set({
			label,
		});

		button.set({
			icon,
			tooltip: true,
			isToggleable: true,
		});

		button.bind('isOn').to(this.command, binding, (value) => {
			return value.includes(this._getValue(button.label));
		});
		this.listenTo(button, 'execute', () => {
			let options = this.command[binding];
			const value = this._getValue(label);

			// if already exist then remove it otherwise add it
			if (options.includes(value))
				options = options.filter((item) => item !== value);
			else options.push(value);

			this.command[binding] = options;
			button.delegate('execute').to(this, binding);
		});

		return button;
	}

	// create toggle button
	_createToggleButton(label, icon, binding) {
		const button = new ButtonView(this.locale);

		button.set({
			label,
		});

		button.set({
			icon,
			tooltip: true,
			isToggleable: true,
		});

		button.bind('isOn').to(this.command, binding, (value) => {
			return this._getValue(button.label) === value;
		});
		this.listenTo(button, 'execute', () => {
			this.command[binding] = this._getValue(label);
			button.delegate('execute').to(this, binding);
		});

		return button;
	}

	_getValue(label) {
		switch (label) {
			case 'Normal':
				return 'media-normal';
			case 'Auto Size':
				return 'media-auto-size';
			case 'Float Left':
				return 'media-left';
			case 'Float Right':
				return 'media-right';
			case 'Center Block':
				return 'media-full';
			case 'Small Video':
				return 'media-small';
			case 'Medium Video':
				return 'media-medium';
			case 'Large Video':
				return 'media-large';
			case 'Show Controls':
				return 'controls';
			case 'Auto Play':
				return 'data-autoplay';
			case 'Loop Video':
				return 'loop';
			case 'Mute Video':
				return 'muted';
			case 'No Tracking':
				return 'data-notrack';
			default:
				return '';
		}
	}

	// Create simple button such as  close, save, reset
	_createButton(label, className, eventName, icon) {
		const button = new ButtonView(this.locale);

		button.set({
			label,
		});

		if (icon) {
			button.set({
				icon,
				tooltip: true,
			});
		} else {
			button.extendTemplate({
				attributes: {
					// CMS9 added the 'ck-no-icon' class.
					class: className + ' ck-no-icon',
				},
			});
		}

		if (eventName) {
			button.delegate('execute').to(this, eventName);
		}

		return button;
	}

	render() {
		super.render();
	}
}
